import { useEffect, useRef, useState } from "react";
import { BarChart3, Plus, X } from "lucide-react";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ListToolbar } from "@/components/ui/data-table/ListToolbar";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useStripVisible } from "@/features/analytics/useStripVisible";
import { useOpportunityFilterDefs } from "@/features/opportunities/filterDefs";
import { OpportunitiesPage } from "@/features/opportunities/OpportunitiesPage";
import { OpportunityFormDialog } from "@/features/opportunities/OpportunityFormDialog";
import { BoardPage } from "./BoardPage";
import { useBoardParams } from "./useBoardParams";
import { usePipelineScope } from "./usePipelineScope";
import { useBoardDensity } from "./useBoardDensity";
import { ViewOptions } from "./ViewOptions";
import { YearScope } from "./YearScope";
import { exitFullscreen, requestFullscreen, useTvMode } from "./useTvMode";

/**
 * The pipeline surface: one route hosting two views of the same opportunities.
 *
 * The board is the default, which is the book's home screen; `?opportunities:view=list`
 * selects the flat list. Both views read filters from the same `opportunities:*` namespace
 * through the same hook, and both are scoped by the year selector — so switching view
 * preserves the filter set *and* the data set. The book calls consistent filtering the
 * single most important shared behaviour in the product; a toggle that quietly changed which
 * records were in play was the last place that was still untrue.
 *
 * **One control row since 2026-08-25** ([pipeline-header](../../../../../specs/pipeline-header.md)):
 * search and the filter button at the left, then year · view · analytics · Present · create at
 * the right. The headline row that carried only the word "Pipeline" is gone and its `<h1>` is
 * `sr-only` — a screen reader and the document outline still need the heading, and the tab
 * already reads it; it was the *visible* word that was redundant. This route is the one place
 * the header row and the toolbar row merged; every other screen keeps both.
 *
 * The row is rendered here rather than in the views because this component already owns the
 * year scope, the view, the strip toggle, Present and create. Search and the filters read the
 * URL through the same `useBoardParams` the board itself uses — the URL is the source of
 * truth, so two readers of it cannot disagree, which is exactly what threading a child's
 * filter state up through a callback would have risked.
 */
export function PipelinePage(): React.JSX.Element {
  const t = useT();
  const scope = usePipelineScope();
  // The same reader the active view uses, over the same URL: this row writes filters, the
  // view below reads them, and neither holds a copy.
  const filterDefs = useOpportunityFilterDefs();
  const params = useBoardParams(filterDefs);
  const { tv, setTv } = useTvMode();
  // In the URL, like every other part of this view — see `useStripVisible` for why it stopped
  // being a browser-local preference.
  const strip = useStripVisible();
  const [createOpen, setCreateOpen] = useState(false);
  const { density, setDensity } = useBoardDensity();

  useDocumentTitle(t("pipeline.title"));

  // Held in a ref so the Escape listener below can be attached once per TV session rather
  // than re-subscribed on every render of the board underneath it.
  const leave = useRef<() => void>(() => undefined);
  leave.current = () => {
    setTv(false);
    // Fullscreen and TV layout are decoupled on the way *in* (a reload cannot re-enter
    // fullscreen), but leaving deliberately does both: a chrome-less browser with the
    // board's own chrome back would be a state nobody asked for.
    exitFullscreen();
  };

  useEffect(() => {
    if (!tv) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      // A detail modal owns Escape while it is open, and the real address is what says
      // whether one is: the surface tree below still renders against the board's location.
      if (window.location.pathname !== "/") return;
      leave.current();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [tv]);

  return (
    <>
      {/* The heading survives the row that used to show it: the outline and a screen reader
          need it, the tab already carries it, and the board gets the band back. */}
      <h1 className="sr-only">{t("pipeline.title")}</h1>

      {/* Presented, the whole row goes: search, filters and the creating action are exactly
          the chrome a wall display drops. */}
      {!tv && (
        <ListToolbar
          table={params}
          defs={filterDefs}
          searchPlaceholder={t("pipeline.searchPlaceholder")}
          // The chips render above the board and the list instead — see `ListToolbar`.
          chips={false}
          actions={
            <>
              {/* Scope first — what is on screen — then how it is being looked at, then the
                  action upon it. Four icon-only groups collapsed into `ViewOptions` on
                  2026-08-31: eight buttons carrying no visible words between them, each
                  explaining itself only on hover. Year and New opportunity stay out here —
                  scope is not a view option, and the creating action never moves. */}
              <YearScope scope={params.scope} />
              {/*
                Aggregates keeps its own button. It went into the view menu with the rest and
                came straight back out (2026-08-31): it toggles whether a *band of the page* is
                showing, which is a different kind of thing from choosing how the board draws,
                and it is the one of the four a reader flips repeatedly. `aria-pressed` is what
                makes the state readable rather than only visible.
              */}
              <Tooltip content={t("pipeline.analytics")}>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t("pipeline.analytics")}
                  aria-pressed={strip.visible}
                  onClick={strip.toggle}
                  className={cn(strip.visible && "border-primary bg-primary/10 text-primary")}
                >
                  <BarChart3 className="h-4 w-4" aria-hidden />
                </Button>
              </Tooltip>
              <ViewOptions
                view={params.scope.view}
                onViewChange={params.scope.setView}
                density={density}
                onDensityChange={setDensity}
                onPresent={() => {
                  setTv(true);
                  // The same gesture, or the browser refuses: fullscreen needs a user
                  // activation and a later effect would not have one.
                  requestFullscreen();
                }}
              />
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                {t("pipeline.newOpportunity")}
              </Button>
            </>
          }
        />
      )}

      {/* The `tv` class is what scales the active view's type — see index.css. Whichever
          view is on screen wears it; the param applies to both (amended 2026-08-25). */}
      {/* The two scale classes ride the same element, and index.css states their intersection
          explicitly rather than letting 0.85 multiply into a wall display. */}
      <div className={cn(tv && "tv", density === "condensed" && "condensed")}>
        {/*
          The preference is passed down rather than read again in each view: one owner of the
          state, one button that changes it, and no chance of the board and the list disagreeing
          about whether the numbers are on screen. Presented, the strip shows regardless — a
          meeting reads it, and the toggle that could hide it is not on the wall to press.
        */}
        {scope.view === "board" ? (
          <BoardPage onCreate={() => setCreateOpen(true)} stripVisible={strip.visible} />
        ) : (
          <OpportunitiesPage embedded stripVisible={strip.visible} />
        )}
      </div>

      {tv && <ExitPresentation onExit={() => leave.current()} />}

      {createOpen && <OpportunityFormDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </>
  );
}

/**
 * The way out of presentation mode that does not need a keyboard.
 *
 * It fades out while the pointer is still, because a permanent button in the corner of a
 * wall display is a permanent distraction — and it never fades from the *keyboard*, which is
 * why focus brings it back rather than only movement.
 */
function ExitPresentation({ onExit }: { onExit: () => void }): React.JSX.Element {
  const t = useT();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let timer = window.setTimeout(() => setVisible(false), 3000);

    const onPointerMove = (): void => {
      setVisible(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setVisible(false), 3000);
    };

    window.addEventListener("pointermove", onPointerMove);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointermove", onPointerMove);
    };
  }, []);

  return (
    // The hint names the keyboard way out as well as the pointer one, which is exactly the
    // kind of thing a native `title` never showed to the reader who needed it.
    <Tooltip content={t("pipeline.leavePresentationHint")}>
      <button
        type="button"
        onClick={onExit}
        aria-label={t("pipeline.leavePresentation")}
        className={cn(
          "bg-card/90 text-muted-foreground hover:text-foreground focus-visible:ring-ring fixed top-4 right-4 z-30 rounded-full border p-2 shadow-md backdrop-blur transition-opacity focus-visible:ring-2 focus-visible:outline-none",
          visible ? "opacity-100" : "opacity-0 focus-visible:opacity-100",
        )}
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
    </Tooltip>
  );
}
